import { useMemo } from "react";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Box,
  Checkbox,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { CircleCheckGreenIcon } from "@braintrust/braintrust-ui-components/Icons";
import { CircleUncheckedIcon } from "@braintrust/braintrust-ui-components/Icons";
import {
  FilterPopoverFooter,
  FilterPopoverFormLayoutWrapper,
} from "@js/apps/employer/components/filter-popover";
import type { RhfForm } from "@js/rhf/types";

import { feedbackParamsSchema } from "../../config";

import styles from "./styles.module.scss";

type FormType = RhfForm<typeof feedbackParamsSchema>;
type FormValues = FormType["Values"];
type DefaultFormValues = FormType["DefaultValues"];

export type TalentFeedbackFormProps = {
  values: DefaultFormValues;
  onApply: (values: FormValues) => void;
};

export const TalentFeedbackForm = ({
  values,
  onApply,
}: TalentFeedbackFormProps) => {
  const defaultValues = useMemo(() => {
    return {
      has_been_hired: !!values?.has_been_hired,
      high_rating: !!values?.high_rating,
    };
  }, [values?.high_rating, values?.has_been_hired]);

  const { handleSubmit, control, reset } = useForm<FormValues>({
    resolver: zodResolver(feedbackParamsSchema),
    defaultValues,
  });

  const onSubmit: SubmitHandler<FormValues> = (formValues) => {
    onApply(formValues);
  };

  const onReset = () => {
    reset({ has_been_hired: null, high_rating: null });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FilterPopoverFormLayoutWrapper
        formBody={
          <Stack sx={{ gap: 2 }}>
            <Controller
              name="has_been_hired"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomCheckbox
                  title="Previously Hired"
                  description="Talent who have been hired on the platform"
                  value={!!value}
                  onChange={onChange}
                />
              )}
            />
            <Controller
              name="high_rating"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomCheckbox
                  title="High Rating"
                  description="Talent with a rating of 4 stars or higher"
                  value={!!value}
                  onChange={onChange}
                />
              )}
            />
          </Stack>
        }
        formFooter={
          <FilterPopoverFooter
            onReset={onReset}
            applyButtonVariant="dark-violet"
          />
        }
      />
    </form>
  );
};

type CustomCheckboxProps = {
  title: string;
  description: string;
  value: boolean;
  onChange: (arg: boolean) => void;
};
const CustomCheckbox = ({
  title,
  description,
  value,
  onChange,
}: CustomCheckboxProps) => {
  return (
    <Box className={styles.checkboxContainer}>
      <Checkbox
        className={styles.checkbox}
        labelVariant="transparent"
        label={
          <>
            <Typography
              component="h3"
              variant="label"
              size="large"
              fontWeight={500}
            >
              {title}
            </Typography>
            <Typography
              variant="label"
              component="span"
              size="small"
              fontWeight={400}
            >
              {description}
            </Typography>
          </>
        }
        icon={<CircleUncheckedIcon />}
        checkedIcon={<CircleCheckGreenIcon />}
        onChange={(_ev, checked) => onChange(checked)}
        checked={value}
      />
    </Box>
  );
};
